import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import logo from '../assets/logo.jpeg'
import {
  LayoutDashboard,
  FileText,
  Plus,
  User,
  LogOut,
  Menu,
  X,
  Settings,
  Inbox,
  CreditCard,
  ChevronDown,
  ChevronRight,
  List,
  Edit,
  DownloadCloud,
  Star,
  AlertTriangle,
  UserCheck,
  Calendar,
  Flame,
  IndianRupee,
  CreditCardIcon,
  Play,
  PartyPopper,
  Bell,
  MessageSquare,
  MapPin,
  Image as ImageIcon,
  Heart,
  BookOpen
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'

function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [expandedItems, setExpandedItems] = useState({})
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const handleLogout = () => {
    logout()
    toast.success('Logged out successfully')
    navigate('/login')
  }

  const toggleExpanded = (itemName) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }))
  }

  const navigation = [
    {
      name: 'Dashboard',
      href: '/',
      icon: LayoutDashboard
    },
    {
      name: 'Users',
      href: '/users',
      icon: User
    },
    {
      name: 'Pandits',
      icon: UserCheck,
      isParent: true,
      subItems: [
        { name: 'All Pandits', href: '/pandits', icon: List },
        { name: 'Featured Pandits', href: '/featured-pandits', icon: Star },
        { name: 'Pending Verification', href: '/unverified-pandits', icon: AlertTriangle }
      ]
    },
    {
      name: 'Yajman',
      icon: UserCheck,
      href: '/yajman',
    },
    {
      name: 'Bookings',
      href: '/bookings',
      icon: Calendar
    },
    {
      name: 'Services',
      href: '/services',
      icon: Flame
    },
    {
      name: 'Live Streams',
      href: '/livestreams',
      icon: Play
    },
    {
      name: 'Festivals',
      href: '/festivals',
      icon: PartyPopper
    },
    {
      name: 'Testimonials',
      href: '/testimonials',
      icon: MessageSquare
    },
    {
      name: 'Lagna Muhurats',
      href: '/muhurats',
      icon: Calendar
    },
    {
      name: 'Tirth Yatra',
      href: '/tirth-sthals',
      icon: MapPin
    },
    {
      name: 'Banners',
      href: '/banners',
      icon: ImageIcon
    },
    {
      name: 'Puja Management',
      icon: Star, // Using Star as placeholder if Flower not available, or I'll add Flower to imports
      isParent: true,
      subItems: [
        { name: 'Categories', href: '/puja/categories', icon: List },
        { name: 'Pujas', href: '/pujas', icon: Flame }
      ]
    },
    {
      name: 'Financial',
      href: '/financial',
      icon: IndianRupee
    },
    {
      name: 'Donations',
      href: '/donations',
      icon: Heart
    },
    {
      name: 'E-library',
      href: '/elib',
      icon: FileText,
      isParent: true,
      subItems: [
        { name: 'All E-Books', href: '/elib', icon: List },
        { name: 'Add E-Books', href: '/elib/add', icon: Plus },
      ]
    },
    {
      name: 'Refund',
      href: '/refund',
      icon: CreditCardIcon
    },
    // {
    //   name: 'Broadcast',
    //   href: '/broadcast',
    //   icon: Bell
    // },
    {
      name: 'Vichar Management',
      href: '/vichar-management',
      icon: BookOpen
    },
    {
      name: 'Contact Options',
      href: '/contact',
      icon: Settings
    }
    // { 
    //   name: 'Reports', 
    //   href: '/reports', 
    //   icon: DownloadCloud 
    // },
    // { 
    //   name: 'Settings', 
    //   href: '/settings', 
    //   icon: Settings 
    // },
  ]

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true
    if (path !== '/' && location.pathname.startsWith(path)) return true
    return false
  }

  const isParentActive = (subItems) => {
    return subItems.some(subItem => isActive(subItem.href))
  }

  const renderNavigationItem = (item) => {
    const Icon = item.icon

    if (item.isParent) {
      const isExpanded = expandedItems[item.name]
      const isParentItemActive = isParentActive(item.subItems)

      return (
        <div key={item.name}>
          <button
            onClick={() => toggleExpanded(item.name)}
            className={`
              w-full group flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200
              ${isParentItemActive
                ? 'bg-primary text-white'
                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }
            `}
          >
            <div className="flex items-center">
              <Icon className={`
                mr-3 h-5 w-5 flex-shrink-0
                ${isParentItemActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-500'}
              `} />
              {item.name}
            </div>
            {isExpanded ? (
              <ChevronDown className={`h-4 w-4 ${isParentItemActive ? 'text-white' : 'text-gray-400'}`} />
            ) : (
              <ChevronRight className={`h-4 w-4 ${isParentItemActive ? 'text-white' : 'text-gray-400'}`} />
            )}
          </button>

          {isExpanded && (
            <div className="ml-6 mt-1 space-y-1">
              {item.subItems.map((subItem) => {
                const SubIcon = subItem.icon
                return (
                  <Link
                    key={subItem.name}
                    to={subItem.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200
                      ${isActive(subItem.href)
                        ? 'bg-primary/10 text-primary border-l-2 border-primary'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <SubIcon className={`
                      mr-3 h-4 w-4 flex-shrink-0
                      ${isActive(subItem.href) ? 'text-primary' : 'text-gray-400 group-hover:text-gray-500'}
                    `} />
                    {subItem.name}
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      )
    }

    return (
      <Link
        key={item.name}
        to={item.href}
        onClick={() => setSidebarOpen(false)}
        className={`
          group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200
          ${isActive(item.href)
            ? 'bg-primary text-white'
            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
          }
        `}
      >
        <Icon className={`
          mr-3 h-5 w-5 flex-shrink-0
          ${isActive(item.href) ? 'text-white' : 'text-gray-400 group-hover:text-gray-500'}
        `} />
        {item.name}
      </Link>
    )
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="absolute inset-0 bg-gray-600 opacity-75"></div>
        </div>
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center">
              <img src={logo} alt="Logo" />
            </div>
            <div className="text-lg font-bold text-gray-900">Admin Panel</div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Navigation */}
        <nav className="flex-1 overflow-y-auto mt-6 px-3">
          <div className="space-y-1 pb-4">
            {navigation.map((item) => renderNavigationItem(item))}
          </div>
        </nav>

        {/* User section */}
        <div className="flex-shrink-0 p-4 bg-white border-t border-gray-200">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.name}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
          >
            <LogOut className="mr-3 h-4 w-4 text-gray-400" />
            Sign out
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="flex-1 lg:flex lg:items-center lg:justify-between">
              <h1 className="text-2xl font-bold text-gray-900">
                Vaidik Sanskar - Admin
              </h1>

              <div className="hidden lg:flex items-center space-x-4">
                <div className="text-sm text-gray-500">
                  Welcome back, {user?.name}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto bg-gray-50">
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}

export default Layout
